
import Component from 'vue-class-component';
import SecondaryLineBarChartFilterable from '@/components/reports-v3/components/filterables/SecondaryLineBarChartFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import echarts from 'echarts';
import moment, {Moment, MomentInput} from 'moment/moment';
import gdbx from '@/store/modules/gdbx';
import {DataViewFormatter} from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import {GobiColor} from '@/helpers/color';
import SecondaryLineBarChart from '@/components/reports-v3/components/elements/charts/SecondaryLineBarChart.vue';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import {TableItemFormatter} from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import { getTableName, TIMEZONE_OFFSET_MS } from '@/worker/db/sqlHelpers';

@Component({
  components: {
    TableFilterable,
    SecondaryLineBarChart,
    SecondaryLineBarChartFilterable,
  },
})
export default class TiarcoChemicalMYearlyComparison extends FilterWidget {

  public get currencySymbol() {
    return gdbx.currencySymbol;
  }

  public get decimal() {
    return gdbx.numDecimal;
  }

    public get currentYearRange(): [number, number] {
        const dateFormat = 'DD/MM/YYYY';

        const accountingMonth = moment(this.accountingDate, dateFormat).month();
        const selectedDate = moment(this.selectedAsOfDate);
        const selectedYear = selectedDate.year();
        const selectedMonth = selectedDate.month();

        const startYear = selectedMonth < accountingMonth ? selectedYear - 1 : selectedYear;

        const startDate = moment()
            .year(startYear)
            .month(accountingMonth)
            .startOf('month')
            .valueOf();

        const endDate = moment(this.selectedAsOfDate).endOf('month').valueOf();
        return [startDate, endDate];
    }

  public get pastYearRange(): [number, number] {
    return [
        moment(this.currentYearRange[0]).add(-1, 'year').valueOf(),
        moment(this.currentYearRange[1]).add(-1, 'year').valueOf(),
    ];
  }

  public get titleName(): string {
    return 'Yearly Comparison (Same Period)';
  }

  public get dataViewFormatter() {
    return DataViewFormatter.salesTracker;
  }

  public get expensiveHook() {
    const {
      selectedDateRange,
      selectedAsOfDate,
      currentYearRange,
      pastYearRange,
        selectedProjects,
    } = this;
    return JSON.stringify([
      selectedDateRange,
      selectedAsOfDate,
      currentYearRange,
      pastYearRange,
        selectedProjects,
    ]);
  }

  public get monthValuesCurrent() {
    const [d0, d1] = this.currentYearRange.map((d) => moment(d));
    const period = d1.diff(d0, 'month') + 1;
    const result: Moment[] = [];
    for (let i = 0; i < period; i++) {
      result.push(d0.clone());
      d0.add(1, 'month');
    }
    return result;
  }

  public get monthValuesPast() {
    const [d0, d1] = this.pastYearRange.map((d) => moment(d));
    const period = d1.diff(d0, 'month') + 1;
    const result: Moment[] = [];
    for (let i = 0; i < period; i++) {
      result.push(d0.clone());
      d0.add(1, 'month');
    }
    return result;
  }

  public filterIds: Array<
      | 'date'
      | 'month'
      | 'dateAsOf'
      | 'dateRange'
      | 'stockItems'
      | 'agents'
      | 'customers'
      | 'suppliers'
      | 'project'
  > = ['suppliers', 'agents', 'dateAsOf', 'month', 'project'];

  public xAxisData: string[] = [];
  public series: echarts.ECharts[] | any[] = [];
  public series2: echarts.ECharts[] | any[] = [];

  public tableFields: object[] = [];

  public tableItems: object[] = [];
  public accountingDate: string = '';

  public exportName(title: string, range: number[]) {
    return title + ' from ' +
        moment(range[0] as MomentInput).format('DD MMM YY') +
        ' - ' +
        moment(range[1] as MomentInput).format('DD MMM YY');
  }

  public async expensiveCalc() {
    const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
    const table = getTableName('transactions');
    const systemTable = getTableName('system');
    const amt = 'TRY_CAST("AMOUNT" AS DOUBLE)';
    const dateExpr = 'TRY_CAST("DOCDATE" AS BIGINT)';

    let projectFilter = '';
    if (this.selectedProjects.length > 0) {
      const list = this.selectedProjects
          .map((p) => `'${escapeStr(p)}'`).join(',');
      projectFilter = ` AND "PROJECT" IN (${list})`;
    }

    const jobId = this.currentJobId;
    const queryHead = await this._ensureTablesLoaded(
        ['transactions', 'system', 'stockBalances'],
    );
    this.checkInterrupted(jobId);

    // Load accounting date
    const systemResult = await queryHead.rawQuery(
        `SELECT "VAL" FROM ${systemTable} LIMIT 1`,
    );
    this.accountingDate = systemResult[0] && systemResult[0].VAL || '';

    if (this.accountingDate) {
      const curRange = this.currentYearRange;
      const pastRange = this.pastYearRange;

      this.generateLoadingText(0.3);

      // Parallel: 2 yearly totals + 2 sets of stock movements
      const [curResult, pastResult, stockMovements1, stockMovements2]
          = await Promise.all([
              queryHead.rawQuery(`
                  SELECT "ACCTYPE" as accType,
                      SUM(${amt}) as total
                  FROM ${table}
                  WHERE ${dateExpr}
                      BETWEEN ${curRange[0]} AND ${curRange[1]}
                      AND "ACCTYPE" IN ('SL','SA','CO','EP','OI','EO')
                      ${projectFilter}
                  GROUP BY accType
              `),
              queryHead.rawQuery(`
                  SELECT "ACCTYPE" as accType,
                      SUM(${amt}) as total
                  FROM ${table}
                  WHERE ${dateExpr}
                      BETWEEN ${pastRange[0]} AND ${pastRange[1]}
                      AND "ACCTYPE" IN ('SL','SA','CO','EP','OI','EO')
                      ${projectFilter}
                  GROUP BY accType
              `),
              Promise.all(
                  this.monthValuesCurrent.map(async (m) => {
                      return await this._getStockMovement(queryHead, [
                          m.valueOf(),
                          m.clone().endOf('month').valueOf(),
                      ], this.selectedProjects as [string]);
                  }),
              ),
              Promise.all(
                  this.monthValuesPast.map(async (m) => {
                      return await this._getStockMovement(queryHead, [
                          m.valueOf(),
                          m.clone().endOf('month').valueOf(),
                      ], this.selectedProjects as [string]);
                  }),
              ),
          ]);
      this.checkInterrupted(jobId);

      // Build accType lookup for each year
      const cur: Record<string, number> = {};
      for (const row of curResult) {
          cur[row.accType] = Number(row.total) || 0;
      }
      const past: Record<string, number> = {};
      for (const row of pastResult) {
          past[row.accType] = Number(row.total) || 0;
      }

      const stockBalance1 = stockMovements1.reduce((a, b) => a + b, 0);
      const stockBalance2 = stockMovements2.reduce((a, b) => a + b, 0);

      this.generateLoadingText(0.7);

      // Current year values
      const sl1 = cur.SL || 0;
      const sa1 = cur.SA || 0;
      const co1 = cur.CO || 0;
      const ep1 = cur.EP || 0;
      const oi1 = cur.OI || 0;
      const eo1 = cur.EO || 0;
      // Past year values
      const sl2 = past.SL || 0;
      const sa2 = past.SA || 0;
      const co2 = past.CO || 0;
      const ep2 = past.EP || 0;
      const oi2 = past.OI || 0;
      const eo2 = past.EO || 0;

      // Chart series
      const grossSales = {
        name: 'Gross Sales',
        data: [sl1 * -1, sl2 * -1],
        type: 'bar',
        barWidth: '10%',
        barGap: '10%',
        itemStyle: { color: '#404040' },
        smooth: true,
      };

      const grossProfit = {
        name: 'Gross Profit',
        data: [
            (sl1 * -1 + sa1 * -1)
                + (co1 * -1 + stockBalance1 * -1),
            (sl2 * -1 + sa2 * -1)
                + (co2 * -1 + stockBalance2 * -1),
        ],
        type: 'bar',
        barWidth: '10%',
        barGap: '10%',
        itemStyle: { color: GobiColor.COLOR1 },
        smooth: true,
      };

      const otherIncome = {
        name: 'Other Income',
        data: [oi1 * -1, oi2 * -1],
        type: 'bar',
        barWidth: '10%',
        barGap: '10%',
        itemStyle: { color: '#4FA89D' },
        smooth: true,
      };

      const cost = {
        name: 'Net COGS',
        data: [co1 + stockBalance1, co2 + stockBalance2],
        type: 'bar',
        barWidth: '10%',
        barGap: '10%',
        itemStyle: { color: '#63AED8' },
        smooth: true,
      };

      const expenses = {
        name: 'Expenses',
        data: [ep1, ep2],
        type: 'bar',
        barWidth: '10%',
        barGap: '10%',
        itemStyle: { color: '#F4DE96' },
        smooth: true,
      };

      const netProfit = {
        name: 'Net Profit',
        data: [
            grossProfit.data[0] + otherIncome.data[0] - expenses.data[0],
            grossProfit.data[1] + otherIncome.data[1] - expenses.data[1],
        ],
        type: 'bar',
        barWidth: '10%',
        barGap: '10%',
        itemStyle: { color: GobiColor.COLOR4 },
        smooth: true,
      };

      const forex = {
        name: 'Realised/Unrealised Forex',
        data: [
            Math.ceil(eo1) * -1,
            Math.ceil(eo2) * -1,
        ],
        type: 'bar',
        barWidth: '10%',
        barGap: '10%',
        itemStyle: { color: '#E89246' },
        smooth: true,
      };

      const npAfterEO = {
        name: 'NP after EO',
        data: [
            Math.ceil(netProfit.data[0])
                + Math.ceil(eo1) * -1,
            Math.ceil(netProfit.data[1])
                + Math.ceil(eo2) * -1,
        ],
        type: 'bar',
        barWidth: '10%',
        barGap: '10%',
        itemStyle: { color: '#A2670D' },
        smooth: true,
      };

      this.xAxisData = [
        moment(curRange[1]).year().valueOf().toString(),
        moment(pastRange[1]).year().valueOf().toString(),
      ];

      this.series = [grossSales, cost, grossProfit, otherIncome,
          expenses, forex, npAfterEO];

      this.tableFields = [
        { key: 'date_range', sortable: false },
        {
          key: 'gross_sales',
          sortable: false,
          formatter: TableItemFormatter.currencySplit,
        },
        {
          key: 'net_COGS',
          sortable: false,
          formatter: TableItemFormatter.currencySplit,
        },
        {
          key: 'g._p.',
          sortable: false,
          formatter: TableItemFormatter.currencySplit,
        },
        {
          key: 'g._p.%',
          sortable: false,
          formatter: TableItemFormatter.percentage,
        },
        {
          key: 'other_income',
          sortable: false,
          formatter: TableItemFormatter.currencySplit,
        },
        {
          key: 'expenses',
          sortable: false,
          formatter: TableItemFormatter.currencySplit,
        },
        {
          key: 'net_profit',
          sortable: false,
          formatter: TableItemFormatter.currencySplit,
        },
        {
          key: 'n._p.%',
          sortable: false,
          formatter: TableItemFormatter.percentage,
        },
        {
          key: 'Realised_/_Unrealised_forex',
          sortable: false,
          formatter: TableItemFormatter.currencySplit,
        },
        {
          key: 'np_after_eo',
          sortable: false,
          formatter: TableItemFormatter.currencySplit,
        },
        {
          key: 'n._p.%_*',
          sortable: false,
          formatter: TableItemFormatter.percentage,
        },
      ];

      this.tableItems = [
        {
          'date_range': moment(curRange[1]).year().valueOf(),
          'gross_sales': Math.ceil(grossSales.data[0]),
          'net_COGS': Math.ceil(cost.data[0]),
          'g._p.': Math.ceil(grossProfit.data[0]),
          'g._p.%': Math.ceil(grossProfit.data[0] / grossSales.data[0] * 100),
          'other_income': Math.ceil(otherIncome.data[0]),
          'expenses': Math.ceil(expenses.data[0]),
          'net_profit': Math.ceil(netProfit.data[0]),
          'n._p.%': Math.ceil(netProfit.data[0] / grossSales.data[0] * 100),
          'Realised_/_Unrealised_forex': Math.ceil(eo1) * -1,
          'np_after_eo': Math.ceil(netProfit.data[0])
              + Math.ceil(eo1) * -1,
          'n._p.%_*': Math.ceil(netProfit.data[0]
              + Math.ceil(eo1) * -1)
              / grossSales.data[0] * 100,
        },
        {
          'date_range': moment(pastRange[1]).year().valueOf(),
          'gross_sales': Math.ceil(grossSales.data[1]),
          'net_COGS': Math.ceil(cost.data[1]),
          'g._p.': Math.ceil(grossProfit.data[1]),
          'g._p.%': Math.ceil(grossProfit.data[1] / grossSales.data[1] * 100),
          'other_income': Math.ceil(otherIncome.data[1]),
          'expenses': Math.ceil(expenses.data[1]),
          'net_profit': Math.ceil(netProfit.data[1]),
          'n._p.%': Math.ceil(netProfit.data[1] / grossSales.data[1] * 100),
          'Realised_/_Unrealised_forex': Math.ceil(eo2) * -1,
          'np_after_eo': Math.ceil(netProfit.data[1])
              + Math.ceil(eo2) * -1,
          'n._p.%_*': Math.ceil(netProfit.data[1]
              + Math.ceil(eo2) * -1)
              / grossSales.data[1] * 100,
        },
      ];

      this.generateLoadingText(1);

      this.saveHistory(
          'series',
          'series2',
          'xAxisData',
          'xAxisDate2',
          'tableFields',
          'tableItems',
      );
    }
  }

}
